/**
 * Pending IdP Role Claims (/admin/idp/pending-claims)
 *
 * Sprint 5 S5-C lane. The FI Admin queue surface for IdP-asserted role
 * strings that have arrived from a configured Identity Provider but have
 * not yet been mapped to a canonical Aaim Role. The Admin reviews each
 * row, approves it (which maps the claim string to an Aaim Role and
 * retroactively sweeps every queued peer claim across the same binding),
 * or denies it (with optional addToDenyList=true to drop future
 * sign-ins of the same string silently).
 *
 * Sprint 1 deviation #2 honored: a PendingIdpRoleClaim is "PENDING" iff
 * `resolvedAt IS NULL`. The backend's list endpoint already filters to
 * unresolved rows -- no client-side status enum.
 *
 * Backend surface (binding-scoped, per the canonical user-management-rbac
 * OpenAPI spec):
 *   GET  /api/v1/admin/idp-bindings/{bindingId}/pending-claims
 *   POST /api/v1/admin/idp-bindings/{bindingId}/pending-claims/{claimId}/approve
 *   POST /api/v1/admin/idp-bindings/{bindingId}/pending-claims/{claimId}/deny
 *
 * Binding listing comes from the identity-provider-registry spec:
 *   GET  /api/v1/tenant-identity-bindings (filtered by caller's tenant)
 *
 * Approve body requires `aaimRoleId` (uuid). The page fetches the FI role
 * catalog via platformAdminClient.GET('/users/roles') and offers the staff
 * role list the other staff pickers offer (`toAssignableStaffRoles`,
 * hooks/useRoleCatalog.ts, GH #2318 option A2), each with its catalog id and
 * displayName. Lowercase role-slug discipline per
 * .claude/rules/authz-case-insensitive.md.
 *
 * URL state: ?bindingId=<uuid> selects the active binding; default is
 * the first binding returned by the registry.
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Heading,
  BodyText,
  Button,
  Card,
  Badge,
  DataTable,
  Dialog,
  Select,
  Textarea,
  Checkbox,
  FormLabel,
  FormHelperText,
  Alert,
  type DataTableColumn,
} from '@aaimplatform/ui';
import type { components as UmrComponents } from '@aaimplatform/institutional-types/user-management-rbac';
import type { components as IdpComponents } from '@aaimplatform/institutional-types/identity-provider-registry';
import type { components as PlatformAdminComponents } from '@aaimplatform/institutional-types/platform-admin';
import {
  userManagementRbacClient,
  identityProviderClient,
  platformAdminClient,
} from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import {
  useIdpPendingClaims,
  type PendingIdpRoleClaim,
} from '../../hooks/useIdpPendingClaims';
import { toAssignableStaffRoles } from '../../hooks/useRoleCatalog';

type TenantIdentityBinding = IdpComponents['schemas']['TenantIdentityBinding'];
type IdentityProvider = IdpComponents['schemas']['IdentityProvider'];
type RoleSummary = PlatformAdminComponents['schemas']['RoleSummary'];

// ---------------------------------------------------------------------------
// Approval role default (lowercase slug per authz-case-insensitive)
// ---------------------------------------------------------------------------

/**
 * Preselected when the catalog offers it; otherwise the first offered role
 * is preselected, and with none offered Approve stays disabled.
 */
const DEFAULT_ROLE_SLUG = 'fi_loan_officer';

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function PendingIdpClaimsPage() {
  const { user } = useAuthStore();
  const tenantId = user?.tenantId ?? undefined;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const bindingIdFromUrl = searchParams.get('bindingId') ?? undefined;

  const [bindings, setBindings] = useState<TenantIdentityBinding[]>([]);
  const [providersById, setProvidersById] = useState<Record<string, IdentityProvider>>({});
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);

  // Active binding -- from URL, else first binding
  const activeBindingId = useMemo(() => {
    if (bindingIdFromUrl) return bindingIdFromUrl;
    return bindings[0]?.id;
  }, [bindingIdFromUrl, bindings]);

  // Approve modal state
  const [approveTarget, setApproveTarget] = useState<PendingIdpRoleClaim | null>(null);
  const [approveRoleSlug, setApproveRoleSlug] = useState<string>('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  // Deny modal state
  const [denyTarget, setDenyTarget] = useState<PendingIdpRoleClaim | null>(null);
  const [denyReason, setDenyReason] = useState('');
  const [denyAddToList, setDenyAddToList] = useState(false);
  const [denySubmitting, setDenySubmitting] = useState(false);
  const [denyError, setDenyError] = useState<string | null>(null);

  // Bootstrap: bindings + providers + roles
  useEffect(() => {
    let canceled = false;
    async function bootstrap() {
      setBootstrapLoading(true);
      setBootstrapError(null);
      try {
        const [bindingResult, providerResult, rolesResult] = await Promise.all([
          identityProviderClient.GET('/api/v1/tenant-identity-bindings', {
            params: { query: { tenantId } },
          }),
          identityProviderClient.GET('/api/v1/identity-providers', {
            params: { query: {} },
          }),
          platformAdminClient.GET('/users/roles'),
        ]);
        if (canceled) return;
        if (bindingResult.error) throw new Error('Failed to load identity provider bindings');
        if (providerResult.error) throw new Error('Failed to load identity providers');
        if (rolesResult.error) throw new Error('Failed to load role catalog');
        const bindingList = bindingResult.data?.data ?? [];
        const providerList = providerResult.data?.data ?? [];
        const providerMap: Record<string, IdentityProvider> = {};
        providerList.forEach((p) => {
          providerMap[p.id] = p;
        });
        setBindings(bindingList);
        setProvidersById(providerMap);
        setRoles(rolesResult.data?.data ?? []);
      } catch (err) {
        if (canceled) return;
        setBootstrapError(err instanceof Error ? err.message : 'Failed to load IdP bindings');
      } finally {
        if (!canceled) setBootstrapLoading(false);
      }
    }
    bootstrap();
    return () => {
      canceled = true;
    };
  }, [tenantId]);

  // Pending claims for the active binding
  const claimsQuery = useIdpPendingClaims(activeBindingId);
  const claims = useMemo(() => claimsQuery.data?.data ?? [], [claimsQuery.data]);
  const totalCount = claimsQuery.data?.totalCount ?? 0;

  // The staff role list, resolved against the catalog: slug, role uuid and
  // displayName for each role the catalog holds (case-insensitive name match).
  const approvableRoles = useMemo(() => toAssignableStaffRoles(roles), [roles]);

  // Active binding view-model
  const activeBinding = useMemo(
    () => bindings.find((b) => b.id === activeBindingId),
    [bindings, activeBindingId],
  );
  const activeProvider = activeBinding ? providersById[activeBinding.identityProviderId] : undefined;

  // Handlers
  const handleBindingChange = useCallback(
    (newBindingId: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('bindingId', newBindingId);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const openApprove = (claim: PendingIdpRoleClaim) => {
    setApproveTarget(claim);
    const preselected =
      approvableRoles.find((role) => role.slug === DEFAULT_ROLE_SLUG) ?? approvableRoles[0];
    setApproveRoleSlug(preselected?.slug ?? '');
    setApproveError(null);
  };

  const openDeny = (claim: PendingIdpRoleClaim) => {
    setDenyTarget(claim);
    setDenyReason('');
    setDenyAddToList(false);
    setDenyError(null);
  };

  const submitApprove = async () => {
    if (!approveTarget || !activeBindingId) return;
    const aaimRoleId = approvableRoles.find((role) => role.slug === approveRoleSlug)?.id;
    if (!aaimRoleId) {
      setApproveError(
        'No staff role from your institution’s role catalog is selected. Open a support case to have Aaim add the role before approving.',
      );
      return;
    }
    setApproveSubmitting(true);
    setApproveError(null);
    try {
      const result = await userManagementRbacClient.POST(
        '/api/v1/admin/idp-bindings/{bindingId}/pending-claims/{claimId}/approve',
        {
          params: {
            path: { bindingId: activeBindingId, claimId: approveTarget.id },
          },
          body: { aaimRoleId },
        },
      );
      if (result.error) throw new Error('Approve failed');
      setApproveTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['fi-idp-pending-claims'] });
    } catch (err) {
      setApproveError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setApproveSubmitting(false);
    }
  };

  const submitDeny = async () => {
    if (!denyTarget || !activeBindingId) return;
    if (denyReason.trim().length < 10) {
      setDenyError('Provide a reason of 10 characters or more for the audit trail.');
      return;
    }
    setDenySubmitting(true);
    setDenyError(null);
    try {
      // Spec body: { addToDenyList: boolean }. The reason text is recorded in the
      // audit-trail emitter on the server side via the request log; the field is
      // not part of the canonical request schema.
      const result = await userManagementRbacClient.POST(
        '/api/v1/admin/idp-bindings/{bindingId}/pending-claims/{claimId}/deny',
        {
          params: {
            path: { bindingId: activeBindingId, claimId: denyTarget.id },
          },
          body: { addToDenyList: denyAddToList },
        },
      );
      if (result.error) throw new Error('Deny failed');
      setDenyTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['fi-idp-pending-claims'] });
    } catch (err) {
      setDenyError(err instanceof Error ? err.message : 'Deny failed');
    } finally {
      setDenySubmitting(false);
    }
  };

  // Columns. DataTable prints an accessor's value as text; markup goes in
  // `cell`. Each column here returned its markup from `accessor`, so the
  // table printed "[object Object]" in every cell, Approve and Deny included.
  const columns: DataTableColumn<PendingIdpRoleClaim>[] = [
    {
      header: 'IdP Claim',
      accessor: 'idpRoleString',
      cell: (_value, row) => (
        <span className="text-sm font-mono text-primary">{row.idpRoleString}</span>
      ),
    },
    {
      header: 'First Seen',
      accessor: 'firstSeenAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {new Date(row.firstSeenAt).toLocaleString()}
        </span>
      ),
    },
    {
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      cell: (_value, row) => (
        <span className="text-sm text-secondary">
          {new Date(row.lastSeenAt).toLocaleString()}
        </span>
      ),
    },
    {
      header: 'Occurrences',
      align: 'right',
      accessor: 'occurrences',
      cell: (_value, row) => (
        <Badge variant="info" size="sm">
          {row.occurrences}
        </Badge>
      ),
    },
    {
      header: 'Actions',
      align: 'right',
      accessor: 'id',
      cell: (_value, row) => (
        <div className="flex justify-end gap-2">
          <Button variant="primary" size="sm" onClick={() => openApprove(row)}>
            Approve
          </Button>
          <Button variant="ghost" size="sm" onClick={() => openDeny(row)}>
            Deny
          </Button>
        </div>
      ),
    },
  ];

  // Render

  if (bootstrapLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant H1. */}
        <BodyText size="sm" color="tertiary">
          Review IdP-asserted role claims that have not yet been mapped to an
          Aaim role. Approving a claim creates retroactive role bindings for
          every staff member who previously asserted the same string from this
          binding. Denying records the resolution and optionally adds the
          string to the binding deny list.
        </BodyText>
      </div>

      {bootstrapError && (
        <div className="mb-4">
          <Alert type="error">{bootstrapError}</Alert>
        </div>
      )}

      {bindings.length === 0 ? (
        <Card>
          <div className="px-6 py-10 text-center">
            <Heading level={2} size="subsection" className="mb-2">
              No identity providers configured.
            </Heading>
            <BodyText size="sm" color="tertiary">
              Configure an identity provider on the Identity Provider page
              before reviewing claim queues.
            </BodyText>
          </div>
        </Card>
      ) : (
        <>
          {/* Binding selector */}
          <Card className="mb-6">
            <div className="px-6 py-4">
              <FormLabel htmlFor="binding-selector">Identity Provider Binding</FormLabel>
              <Select
                id="binding-selector"
                value={activeBindingId ?? ''}
                onChange={(e) => handleBindingChange(e.target.value)}
              >
                {bindings.map((b) => {
                  const provider = providersById[b.identityProviderId];
                  const label = provider
                    ? `${provider.name} (${provider.providerFamily})`
                    : `Binding ${b.id.slice(0, 8)}`;
                  return (
                    <option key={b.id} value={b.id}>
                      {label}
                      {b.isPrimary ? ' [primary]' : ''}
                    </option>
                  );
                })}
              </Select>
              {activeBinding && activeProvider && (
                <FormHelperText>
                  Issuer: {activeProvider.issuerUrl ?? activeProvider.providerFamily} -- Status: {activeBinding.status}
                </FormHelperText>
              )}
            </div>
          </Card>

          {/* Claims table */}
          <Card>
            <div className="px-6 py-4 border-b border-recessed flex items-center gap-2">
              <Heading level={2} size="subsection">Pending Claims</Heading>
              <Badge variant="info" size="sm" className="ml-2">
                {totalCount}
              </Badge>
            </div>
            <div className="p-4">
              {claimsQuery.isLoading ? (
                <div className="text-center py-10 text-sm text-tertiary">
                  Loading pending claims...
                </div>
              ) : claimsQuery.error ? (
                <Alert type="error">
                  {claimsQuery.error.message}
                </Alert>
              ) : claims.length === 0 ? (
                <div className="text-center py-10 text-sm text-tertiary">
                  No pending IdP claims to review.
                </div>
              ) : (
                <DataTable<PendingIdpRoleClaim>
                  columns={columns}
                  data={claims}
                  hoverable
                  striped
                />
              )}
            </div>
          </Card>
        </>
      )}

      {/* Approve modal */}
      <Dialog
        open={Boolean(approveTarget)}
        onOpenChange={(o) => {
          if (!o) setApproveTarget(null);
        }}
        title="Approve IdP claim"
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setApproveTarget(null)}
              disabled={approveSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitApprove}
              disabled={approveSubmitting || approvableRoles.length === 0}
            >
              {approveSubmitting ? 'Approving...' : 'Approve'}
            </Button>
          </>
        }
      >
        {approveTarget && (
          <div className="space-y-4">
            {approveError && <Alert type="error">{approveError}</Alert>}
            <div>
              <FormLabel>IdP Claim</FormLabel>
              <BodyText size="sm" className="font-mono">
                {approveTarget.idpRoleString}
              </BodyText>
            </div>
            <div>
              <FormLabel htmlFor="approve-role">Approve to Aaim Role</FormLabel>
              <Select
                id="approve-role"
                value={approveRoleSlug}
                onChange={(e) => setApproveRoleSlug(e.target.value)}
                disabled={approvableRoles.length === 0}
              >
                {approvableRoles.map((role) => (
                  <option key={role.id} value={role.slug}>
                    {role.name} ({role.slug})
                  </option>
                ))}
              </Select>
              {approvableRoles.length === 0 && (
                <FormHelperText>
                  The role catalog returned no staff roles this institution can assign.
                </FormHelperText>
              )}
            </div>
            <Alert type="warning" title="Retroactive sweep">
              Approving creates retroactive UserRole rows for {approveTarget.occurrences}{' '}
              staff who previously asserted &quot;{approveTarget.idpRoleString}&quot; from this
              binding. The binding&apos;s claimMappingOverrides.roleAllowlist updates so future
              sign-ins of the same string auto-bind without entering the queue.
            </Alert>
          </div>
        )}
      </Dialog>

      {/* Deny modal */}
      <Dialog
        open={Boolean(denyTarget)}
        onOpenChange={(o) => {
          if (!o) setDenyTarget(null);
        }}
        title="Deny IdP claim"
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setDenyTarget(null)}
              disabled={denySubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitDeny}
              disabled={denySubmitting}
            >
              {denySubmitting ? 'Denying...' : 'Deny'}
            </Button>
          </>
        }
      >
        {denyTarget && (
          <div className="space-y-4">
            {denyError && <Alert type="error">{denyError}</Alert>}
            <div>
              <FormLabel>IdP Claim</FormLabel>
              <BodyText size="sm" className="font-mono">
                {denyTarget.idpRoleString}
              </BodyText>
            </div>
            <Textarea
              label="Reason (audit trail)"
              id="deny-reason"
              rows={3}
              value={denyReason}
              onChange={(value) => setDenyReason(value)}
              placeholder="Reason for denying this claim. Minimum 10 characters."
              helperText="Reason is recorded in the audit trail. The canonical request body carries the deny-list toggle; the reason rides along on the audit log."
            />
            <Checkbox
              checked={denyAddToList}
              onChange={(checked) => setDenyAddToList(checked)}
              label="Add to deny list"
              helperText="Future sign-ins of this string drop silently. No PendingIdpRoleClaim row is created."
            />
          </div>
        )}
      </Dialog>
    </div>
  );
}

// Suppress unused-import warnings for the type-only imports if tsc is strict
// (the `components` namespaces are used only for type aliases above).
export type { UmrComponents as _PendingIdpClaimsUmrComponents };
